/** Week-range and compact time-sheet formatters (v1 formats). */

/** Returns the start of the current week (Monday) as ISO string. */
export const getWeekStart = (date: Date): string => {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  d.setDate(diff)
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

/** Returns the end of the current week (Sunday) as ISO string. */
export const getWeekEnd = (date: Date): string => {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? 0 : 7)
  d.setDate(diff)
  d.setHours(23, 59, 59, 999)
  return d.toISOString()
}

/** Format duration in minutes to "XhYm" (v1: no space, no zero-pad). */
export const formatHours = (totalMinutes: number): string => {
  const total = Math.round(totalMinutes)
  const h = Math.floor(total / 60)
  const m = total % 60
  return `${h}h${m}m`
}

/** Format ISO datetime to "hh:mmAM" (v1: padded hour, no space, e.g. 05:35PM). */
export const formatTime = (iso: string): string => {
  const d = new Date(iso)
  const h24 = d.getHours()
  const h = h24 % 12 === 0 ? 12 : h24 % 12
  const m = String(d.getMinutes()).padStart(2, '0')
  return `${String(h).padStart(2, '0')}:${m}${h24 >= 12 ? 'PM' : 'AM'}`
}

/** Format an ISO date to MM/DD/YYYY (v1's Dates range). */
export const formatShortDate = (iso: string): string => {
  const d = new Date(iso)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${mm}/${dd}/${d.getFullYear()}`
}

/** Format an ISO date to MM/DD (v1's history rows). */
export const formatDayMonth = (iso: string): string => {
  const d = new Date(iso)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${mm}/${dd}`
}

/** "MM/DD hh:mmAM" — v1 shows the date on both ends of a history row. */
export const formatDateTime = (iso: string): string =>
  `${formatDayMonth(iso)} ${formatTime(iso)}`

/** Elapsed minutes of a span; open-ended spans run until now. */
export const spanMinutes = (
  startTime: string,
  endTime?: string | null,
): number => {
  const start = new Date(startTime).getTime()
  const end = endTime ? new Date(endTime).getTime() : Date.now()
  return (end - start) / 60000
}

/** Structural subset of a shift and its entries the totals need. */
type CountableShift = {
  timeClocks: {
    startTime: string
    endTime?: string | null
    voided?: boolean | null
    task?: { id: number; name: string; color: string } | null
  }[]
}

/**
 * Minutes a shift counts for: the sum of its live entries. Voided entries stay
 * visible to the operator but no longer count — and the shift span the API
 * returns already excludes them at the edges, so the entries themselves are
 * the only reliable base to total from.
 */
export const workedMinutes = (shift: CountableShift): number =>
  shift.timeClocks
    .filter((timeClock) => !timeClock.voided)
    .reduce(
      (acc, timeClock) =>
        acc + spanMinutes(timeClock.startTime, timeClock.endTime),
      0,
    )

/** Minutes worked across shifts, voided entries excluded. */
export const totalWorkedMinutes = (shifts: CountableShift[]): number =>
  shifts.reduce((acc, shift) => acc + workedMinutes(shift), 0)

/** Minutes per task across shifts, voided entries excluded. */
export const minutesByTask = (shifts: CountableShift[]) => {
  const byTask = new Map<
    number,
    { id: number; name: string; minutes: number; color: string }
  >()
  for (const shift of shifts) {
    for (const { task, startTime, endTime, voided } of shift.timeClocks) {
      if (!task || voided) continue
      const entry = byTask.get(task.id) ?? {
        id: task.id,
        name: task.name,
        color: task.color,
        minutes: 0,
      }
      entry.minutes += spanMinutes(startTime, endTime)
      byTask.set(task.id, entry)
    }
  }
  return [...byTask.values()]
}
